import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { useExperience } from '../../contexts/ExperienceContext';
import { routeOptions } from '../../data/journeys';
import { brand } from '../../data/palette';
import { Eyebrow } from '../ui/Stage';
import type { Route } from '../../types/filtek';
const ease = [0.16, 1, 0.3, 1] as const;

/** The entry point: one question, three ways in. */
export function EntryStage() {
  const {
    start
  } = useExperience();
  const [hovered, setHovered] = useState<Route | undefined>();
  const [chosen, setChosen] = useState<Route | undefined>();
  const choose = (route: Route) => {
    if (chosen) return;
    setChosen(route);
    window.setTimeout(() => start(route), 580);
  };
  return <motion.section exit={{
    opacity: 0,
    filter: 'blur(18px)'
  }} transition={{
    duration: 0.6,
    ease
  }} className="relative mx-auto flex min-h-screen w-full max-w-[1400px] flex-col justify-between px-6 pb-10 pt-28 sm:px-10 sm:pt-32 lg:px-16" aria-labelledby="entry-title" data-id="element-221" data-mp="l1tghk1gf03w">
      <div data-id="element-222" data-mp="ilqq262p4wwt">
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-223" data-mp="il74mfdu7qe1">
          <Eyebrow accent={brand.green} data-id="element-224" data-mp="nmwh7zgpkc48">Solventum · Filtek Family</Eyebrow>
        </motion.div>

        <motion.h1 id="entry-title" className="display-tight mt-8 max-w-4xl text-display font-light text-enamel" initial={{
        opacity: 0,
        y: 26,
        filter: 'blur(12px)'
      }} animate={{
        opacity: 1,
        y: 0,
        filter: 'blur(0px)'
      }} transition={{
        duration: 1,
        delay: 0.12,
        ease
      }} data-id="element-225" data-mp="sl7p44ng0cjt">
          Where are you with Filtek?
        </motion.h1>

        <motion.p className="mt-8 max-w-md text-lede font-light text-mute" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 0.5
      }} data-id="element-226" data-mp="1b9waerpu8jw4">
          Different cases. Different demands.
          <span className="block text-enamel" data-id="element-227" data-mp="vjwqxv6trffa">Fewer compromises.</span>
        </motion.p>
      </div>

      <motion.div className="mt-14 grid grid-cols-1 border-t border-enamel/10 md:mt-0 md:grid-cols-3" initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.9,
      delay: 0.35,
      ease
    }} data-id="element-228" data-mp="qm4qudw10n00">
        {routeOptions.map((option, index) => {
        const isHovered = hovered === option.id;
        const isChosen = chosen === option.id;
        const dimmed = hovered && !isHovered || chosen && !isChosen;
        return <button key={option.id} type="button" onMouseEnter={() => setHovered(option.id)} onMouseLeave={() => setHovered(undefined)} onFocus={() => setHovered(option.id)} onBlur={() => setHovered(undefined)} onClick={() => choose(option.id)} className="group relative flex min-h-[27vh] flex-col justify-end overflow-hidden border-b border-enamel/10 py-8 text-left md:min-h-[40vh] md:border-b-0 md:border-r md:px-8 md:last:border-r-0 md:[&:first-child]:pl-0" data-id="element-229" data-mp="1o9djqtycwyc1" data-mpi={index}>
              <motion.span className="pointer-events-none absolute left-1/2 top-[72%] h-64 w-64 -translate-x-1/2 rounded-full blur-[70px]" style={{
            backgroundColor: option.accent
          }} initial={false} animate={{
            scale: isChosen ? 16 : isHovered ? 1.3 : 0.45,
            opacity: isChosen ? 0.55 : isHovered ? 0.42 : 0.13
          }} transition={{
            duration: isChosen ? 1 : 0.85,
            ease
          }} data-id="element-230" data-mp="1qu1rqkfvk7b2" />

              <motion.span className="relative block" initial={false} animate={{
            opacity: dimmed ? 0.32 : 1,
            y: isHovered ? -6 : 0
          }} transition={{
            duration: 0.6,
            ease
          }} data-id="element-231" data-mp="1csz44398eoy1">
                <span className="label-mono flex items-center justify-between text-[10px] text-mute" data-id="element-232" data-mp="375p7dblhpai3">
                  {option.index}
                  <ArrowUpRightIcon className="h-4 w-4 text-enamel opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" data-id="element-233" data-mp="125xofxkjesr5" />
                </span>

                <span className="mt-10 block max-w-[16ch] text-[1.75rem] font-light leading-[1.05] tracking-[-0.03em] text-enamel sm:text-[2.1rem] md:mt-16 lg:text-[2.5rem]" data-id="element-234" data-mp="t598gxbl2ibm">
                  {option.label}?
                </span>

                <span className="mt-4 block max-w-[26ch] text-sm font-light leading-relaxed text-mute" data-id="element-235" data-mp="je4sbua68rf7">
                  {option.detail}
                </span>

                <span className="mt-7 block h-px w-full origin-left scale-x-0 transition-transform duration-[700ms] ease-out group-hover:scale-x-100" style={{
              backgroundColor: option.accent
            }} data-id="element-236" data-mp="agkocdkxch0w" />
              </motion.span>
            </button>;
      })}
      </motion.div>
    </motion.section>;
}